import Constants from '@/Constants';
import { useProgramClass } from '@/hooks/useProgramClass';
import { OnChangeProps } from '@/models/typings/ReactDateRange';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { currentDate, getNewDate } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import { differenceInDays } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ClassPeriodModal from '../ClassPeriodModal';
import { PeriodButton } from './ClassPeriodButton';
import { DatePickerPeriod } from './DatePickerPeriod';
import './style.scss';

interface Period {
    startDate: Date | undefined;
    finishDate?: Date | undefined;
}

interface Props {
    startDateForm?: Date;
    finishDateForm?: Date;
    meetingsForm?: WorkshopMeeting[];
    onChange: ({ startDate, finishDate }: Period) => void;
    isWorkshop?: boolean;
}

export enum DateFieldNames {
    startDate = 'startDate',
    finishDate = 'finishDate',
}

const ClassPeriod: React.FC<Props> = ({ startDateForm, finishDateForm, meetingsForm = [], isWorkshop = false, onChange }) => {
    const { classId: classIdString } = useParams<{ courseId: string; classId: string }>();

    const classId: number = Number(classIdString);

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const [showModal, setShowModal] = useState<boolean>(false);

    const [dateFieldName, setDateFieldName] = useState<DateFieldNames>();

    const [startDate, setStartDate] = useState<Date>();

    const [finishDate, setFinishDate] = useState<Date>();

    const { programClass } = useProgramClass({
        programClassId: classId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const enableStartDateButton: boolean =
        !programClass?.startDate || differenceInDays(programClass.startDate || programClass.scheduledStartDate, currentDate()) > 0;

    const enableFinishDateButton: boolean = Boolean(startDateForm && programClass?.program.type !== 'BUILDING');

    const startDateKeyStorageExists: boolean = !!localStorage.getItem(Constants.PROGRAM_CLASS_START_DATE_KEY);

    const clearStartDate = useCallback((): void => {
        if (enableStartDateButton) {
            setStartDate(undefined);
            setFinishDate(undefined);

            if (startDateKeyStorageExists) {
                localStorage.removeItem(Constants.PROGRAM_CLASS_START_DATE_KEY);
            }
        }
    }, [enableStartDateButton]);

    const clearFinishDate = useCallback((): void => {
        if (enableFinishDateButton) {
            setFinishDate(undefined);
        }
    }, [enableFinishDateButton]);

    const selectDate = useCallback(
        (value: OnChangeProps) => {
            if (dateFieldName === DateFieldNames.startDate) {
                setStartDate(value as Date);
                localStorage.setItem(Constants.PROGRAM_CLASS_START_DATE_KEY, value as string);

                if (finishDateForm && value > finishDateForm) {
                    setFinishDate(undefined);
                }
            } else if (dateFieldName === DateFieldNames.finishDate) {
                setFinishDate(value as Date);
            }

            setDateFieldName(undefined);
        },
        [dateFieldName, finishDateForm],
    );

    const initialize = (): void => {
        if (programClass) {
            const { startDate, scheduledStartDate, finishDate } = programClass;

            setStartDate(startDate || scheduledStartDate);
            setFinishDate(finishDate);
        }
    };

    const onPeriodChange = () => {
        onChange({ startDate: getNewDate(startDate), finishDate: getNewDate(finishDate) });
    };

    useEffect(initialize, [programClass]);

    useEffect(onPeriodChange, [startDate, finishDate]);

    return (
        <div className="class-period-container">
            <div className="class-period-container__input-section">
                {isWorkshop && (
                    <span className="class-period-container__input-section__deadline">
                        {strings.DEADLINE}
                        <i className="hint material-icons-outlined icon" onClick={() => setShowModal(true)}>
                            {Constants.icons.ERROR_OUTLINE}
                        </i>
                    </span>
                )}
                <span className="class-period-container__input-section__info">{strings.CLASS_PERIOD}</span>
            </div>

            <div className="class-period-container__buttons">
                <PeriodButton
                    buttonText={strings.START}
                    chooseDateText={strings.CHOOSE_START_DATE}
                    dateForm={startDateForm}
                    enabled={enableStartDateButton}
                    fieldName={DateFieldNames.startDate}
                    onClear={clearStartDate}
                    onClick={() => setDateFieldName(DateFieldNames.startDate)}
                />

                <PeriodButton
                    buttonText={strings.END}
                    chooseDateText={strings.CHOOSE_END_DATE}
                    dateForm={finishDateForm}
                    enabled={enableFinishDateButton}
                    fieldName={DateFieldNames.finishDate}
                    onClear={clearFinishDate}
                    onClick={() => setDateFieldName(DateFieldNames.finishDate)}
                />
            </div>

            <ClassPeriodModal onHide={() => setShowModal(false)} visible={showModal} />

            <DatePickerPeriod
                dateFieldName={dateFieldName}
                finishDate={finishDateForm}
                meetings={meetingsForm}
                onChange={selectDate}
                onHide={() => setDateFieldName(undefined)}
                startDate={startDateForm}
            />
        </div>
    );
};

export default ClassPeriod;
